import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Mic, MicOff, Volume2, VolumeX, Send, X, Loader2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AudioRecorder, playAudioFromBase64 } from "@/utils/audioRecorder";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

interface Question {
  id: string;
  text: string;
  category: string;
  difficulty: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const InterviewPractice = () => {
  const { mode } = useParams<{ mode: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [sessionId, setSessionId] = useState<string>("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [aiVoiceEnabled, setAiVoiceEnabled] = useState(true);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  
  const audioRecorderRef = useRef<AudioRecorder | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const conversationHistoryRef = useRef<Array<{role: string, content: string}>>([]);

  useEffect(() => {
    initializeSession();
  }, [mode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const initializeSession = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      // Load questions based on mode
      let questionQuery = supabase
        .from('interview_questions')
        .select('*')
        .limit(mode === 'quick' ? 5 : 10);

      if (mode === 'quick') {
        questionQuery = questionQuery.eq('difficulty', 'easy');
      }

      const { data: questionsData, error: questionsError } = await questionQuery;
      
      if (questionsError) throw questionsError;
      setQuestions(questionsData || []);

      // Create interview session
      const { data: session, error: sessionError } = await supabase
        .from('interview_sessions')
        .insert({
          user_id: user.id,
          interview_type: mode || 'quick',
          status: 'in-progress'
        })
        .select()
        .single();

      if (sessionError) throw sessionError;
      setSessionId(session.id);

      // Start with AI greeting
      if (questionsData && questionsData.length > 0) {
        const greeting = `Hello! I'm excited to conduct this interview with you today. Let's start with the first question: ${questionsData[0].text}`;
        addAiMessage(greeting);
      }
    } catch (error) {
      console.error('Error initializing session:', error);
      toast({
        title: "Error",
        description: "Failed to start interview session",
        variant: "destructive"
      });
    }
  };

  const addAiMessage = async (content: string) => {
    const message: Message = {
      role: 'assistant',
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, message]);
    conversationHistoryRef.current.push({ role: 'assistant', content });

    // Play AI voice if enabled
    if (aiVoiceEnabled && !isAiSpeaking) {
      try {
        setIsAiSpeaking(true);
        const { data, error } = await supabase.functions.invoke('text-to-speech', {
          body: { text: content, voice: 'nova' }
        });

        if (error) throw error;
        if (data?.audioContent) {
          await playAudioFromBase64(data.audioContent);
        }
      } catch (error) {
        console.error('Error playing AI voice:', error);
      } finally {
        setIsAiSpeaking(false);
      }
    }
  };

  const handleStartRecording = async () => {
    try {
      if (!audioRecorderRef.current) {
        audioRecorderRef.current = new AudioRecorder();
      }
      await audioRecorderRef.current.start();
      setIsRecording(true);
      toast({
        title: "Recording started",
        description: "Speak clearly into your microphone"
      });
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: "Error",
        description: "Failed to start recording. Please check microphone permissions.",
        variant: "destructive"
      });
    }
  };

  const handleStopRecording = async () => {
    if (!audioRecorderRef.current) return;

    try {
      setIsRecording(false);
      setIsProcessing(true);
      
      const base64Audio = await audioRecorderRef.current.stop();

      // Transcribe audio
      const { data, error } = await supabase.functions.invoke('speech-to-text', {
        body: { audio: base64Audio }
      });

      if (error) throw error;
      
      if (data?.text) {
        setInputMessage(data.text);
        toast({
          title: "Transcription complete",
          description: "You can edit the text before sending"
        });
      }
    } catch (error) {
      console.error('Error stopping recording:', error);
      toast({
        title: "Error",
        description: "Failed to transcribe audio",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isProcessing) return;

    const userMessage: Message = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    conversationHistoryRef.current.push({ role: 'user', content: inputMessage });
    
    const currentQuestion = questions[currentQuestionIndex];
    const messageToSave = inputMessage;
    setInputMessage("");
    setIsProcessing(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Save user message
      await supabase
        .from('interview_answers')
        .insert({
          session_id: sessionId,
          question_id: currentQuestion.id,
          user_id: user.id,
          answer_text: messageToSave,
          duration: 0
        });

      // Get AI response
      const { data: chatData, error: chatError } = await supabase.functions.invoke('interview-chat', {
        body: {
          message: messageToSave,
          conversationHistory: conversationHistoryRef.current.slice(-10), // Last 10 messages for context
          questionContext: currentQuestion
        }
      });

      if (chatError) throw chatError;

      if (chatData?.message) {
        await addAiMessage(chatData.message);
      }

      // Check if we should move to next question after a few exchanges
      const userMessagesCount = conversationHistoryRef.current.filter(m => m.role === 'user').length;
      if (userMessagesCount >= 3) {
        // Move to next question
        if (currentQuestionIndex < questions.length - 1) {
          setTimeout(async () => {
            setCurrentQuestionIndex(prev => prev + 1);
            conversationHistoryRef.current = [];
            const nextQuestion = questions[currentQuestionIndex + 1];
            await addAiMessage(`Great! Let's move on to the next question: ${nextQuestion.text}`);
          }, 2000);
        } else {
          // Interview complete
          setTimeout(async () => {
            await completeInterview();
          }, 2000);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const completeInterview = async () => {
    try {
      // Generate final feedback for all answers
      const { data: allAnswers } = await supabase
        .from('interview_answers')
        .select('*')
        .eq('session_id', sessionId);

      let totalScore = 0;
      let answersWithScores = 0;

      for (const answer of allAnswers || []) {
        const question = questions.find(q => q.id === answer.question_id);
        if (!question) continue;

        const { data: feedbackData } = await supabase.functions.invoke('generate-interview-feedback', {
          body: {
            question: question.text,
            answer: answer.answer_text,
            category: question.category
          }
        });

        if (feedbackData?.feedback) {
          await supabase
            .from('interview_answers')
            .update({
              overall_score: feedbackData.feedback.overall_score,
              content_score: feedbackData.feedback.content_score,
              structure_score: feedbackData.feedback.structure_score,
              communication_score: feedbackData.feedback.communication_score,
              relevance_score: feedbackData.feedback.relevance_score,
              feedback: feedbackData.feedback
            })
            .eq('id', answer.id);

          totalScore += feedbackData.feedback.overall_score;
          answersWithScores++;
        }
      }

      const avgScore = answersWithScores > 0 ? totalScore / answersWithScores : 0;

      await supabase
        .from('interview_sessions')
        .update({ 
          status: 'completed',
          completed_at: new Date().toISOString(),
          overall_score: avgScore
        })
        .eq('id', sessionId);

      // Update user progress
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        const { data: progress } = await supabase
          .from('interview_progress')
          .select('*')
          .eq('user_id', currentUser.id)
          .maybeSingle();

        if (progress) {
          await supabase
            .from('interview_progress')
            .update({
              total_interviews: (progress.total_interviews || 0) + 1,
              total_questions: (progress.total_questions || 0) + questions.length,
              average_score: ((progress.average_score || 0) * (progress.total_interviews || 0) + avgScore) / ((progress.total_interviews || 0) + 1),
              last_practice_date: new Date().toISOString().split('T')[0],
              updated_at: new Date().toISOString()
            })
            .eq('user_id', currentUser.id);
        } else {
          await supabase
            .from('interview_progress')
            .insert({
              user_id: currentUser.id,
              total_interviews: 1,
              total_questions: questions.length,
              average_score: avgScore,
              last_practice_date: new Date().toISOString().split('T')[0]
            });
        }
      }

      navigate(`/app/ai-interview-prep/results/${sessionId}`);
    } catch (error) {
      console.error('Error completing interview:', error);
      toast({
        title: "Error",
        description: "Failed to complete interview",
        variant: "destructive"
      });
    }
  };

  const handleExit = async () => {
    if (confirm("Are you sure you want to exit? Your progress won't be saved.")) {
      await supabase
        .from('interview_sessions')
        .update({ status: 'abandoned' })
        .eq('id', sessionId);
      navigate('/app/ai-interview-prep');
    }
  };

  if (!questions.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading interview questions...</p>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <>
      <Helmet>
        <title>Interview Practice Session | AI Interview Prep</title>
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        {/* Top Bar */}
        <div className="border-b bg-card px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                {mode === 'quick' ? 'Quick Practice' : 'Job-Specific Interview'}
              </span>
              <span className="text-muted-foreground">
                Question {currentQuestionIndex + 1} of {questions.length}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Label htmlFor="ai-voice" className="text-sm">AI Voice</Label>
                <Switch
                  id="ai-voice"
                  checked={aiVoiceEnabled}
                  onCheckedChange={setAiVoiceEnabled}
                />
                {aiVoiceEnabled ? <Volume2 className="w-4 h-4 text-primary" /> : <VolumeX className="w-4 h-4 text-muted-foreground" />}
              </div>
              <Button variant="ghost" size="sm" onClick={handleExit}>
                <X className="w-4 h-4 mr-2" />
                Exit
              </Button>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="max-w-7xl mx-auto">
            <Progress value={progress} className="h-2" />
          </div>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 px-6 py-8 overflow-y-auto">
          <div className="max-w-4xl mx-auto space-y-6">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <Card className={`max-w-[80%] ${message.role === 'user' ? 'bg-primary/10' : 'bg-card'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {message.role === 'assistant' && (
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm">🤖</span>
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
            {isAiSpeaking && (
              <div className="flex justify-start">
                <Card className="bg-card">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Volume2 className="w-5 h-5 text-primary animate-pulse" />
                      <p className="text-sm text-muted-foreground">Speaking...</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input Area */}
        <div className="border-t bg-card px-6 py-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Textarea
                  placeholder="Type your response or use voice..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  className="min-h-[80px] resize-none"
                  disabled={isRecording || isProcessing}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  variant={isRecording ? "destructive" : "default"}
                  size="icon"
                  onClick={isRecording ? handleStopRecording : handleStartRecording}
                  disabled={isProcessing}
                  className="h-[80px] w-16"
                >
                  {isRecording ? (
                    <MicOff className="w-5 h-5" />
                  ) : (
                    <Mic className="w-5 h-5" />
                  )}
                </Button>
                <Button
                  size="icon"
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isProcessing || isRecording}
                  className="w-16"
                >
                  {isProcessing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </div>
            {isRecording && (
              <p className="text-sm text-primary mt-2 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                Recording... Click the mic button again to stop
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default InterviewPractice;
